/**
 * © 2025 Sixsmith Games. All rights reserved.
 * This software and associated documentation files are proprietary and confidential.
 */

import React from 'react';
import { SignedIn, UserButton } from '@clerk/clerk-react';
import { Link, useLocation } from 'react-router-dom';
import { HomeIcon, BookOpenIcon, MoonIcon, SunIcon } from 'lucide-react';
import { clsx } from 'clsx';
import { useTheme } from '../contexts/ThemeContext';
import { getProductConfig } from '../config/products';
import { isLocalMode } from '../utils/localMode';

export const Navbar: React.FC = () => {
  const location = useLocation();
  const { isDark, toggleTheme } = useTheme();
  const product = getProductConfig();

  return (
    <nav aria-label="Product navigation" className="bg-white border-b border-gray-200 shadow-sm transition-colors dark:bg-slate-900 dark:border-slate-800">
      <div className="container mx-auto px-4">
        <div className="flex items-center justify-between h-16">
          <div className="flex items-center gap-3 lg:gap-8 min-w-0">
            <Link to="/" className="flex items-center space-x-2">
              <BookOpenIcon className="w-8 h-8 text-primary-600" />
              <span className="text-lg sm:text-xl font-bold text-gray-900 dark:text-slate-100">{product.name}</span>
            </Link>

            <div className="hidden md:flex space-x-4">
              <Link
                to="/"
                aria-current={location.pathname === '/' ? 'page' : undefined}
                className={clsx(
                  'flex items-center space-x-2 px-3 py-2 rounded-md text-sm font-medium transition-colors',
                  location.pathname === '/'
                    ? 'bg-primary-100 text-primary-700 dark:bg-primary-600/20 dark:text-blue-200'
                    : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100 dark:text-slate-400 dark:hover:text-slate-100 dark:hover:bg-slate-800'
                )}
              >
                <HomeIcon className="w-4 h-4" />
                <span>{product.navigationLabels.dashboard}</span>
              </Link>
            </div>
          </div>

          <div className="flex items-center gap-2 shrink-0">
            <button
              type="button"
              onClick={toggleTheme}
              className="inline-flex items-center gap-2 rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-100 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200 dark:hover:bg-slate-800"
              aria-label={isDark ? 'Switch to light mode' : 'Switch to dark mode'}
              title={isDark ? 'Switch to light mode' : 'Switch to dark mode'}
            >
              {isDark ? <SunIcon className="w-4 h-4" /> : <MoonIcon className="w-4 h-4" />}
              <span className="hidden sm:inline">{isDark ? 'Light mode' : 'Dark mode'}</span>
            </button>
            {isLocalMode() ? (
              <span className="hidden sm:inline rounded-full border border-amber-200 bg-amber-50 px-3 py-1.5 text-xs font-medium text-amber-700 dark:border-amber-500/30 dark:bg-amber-500/10 dark:text-amber-300">
                Local mode
              </span>
            ) : (
              <SignedIn>
                <UserButton />
              </SignedIn>
            )}
          </div>
        </div>
      </div>
    </nav>
  );
};
